import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import useCurrency from "../hooks/useCurrency";
import CurrencySelector from "../components/CurrencySelector";

const FEATURES = [
  { icon: "🎯", title: "Lead Capture QR", desc: "Scan → Name, Email, Phone form → Redirect. Every lead saved instantly to your dashboard with export to CSV." },
  { icon: "⭐", title: "Smart Review Filter", desc: "4-5 stars go to Google reviews. 1-3 stars get a private form. Protect your reputation automatically." },
  { icon: "📊", title: "Deep Analytics", desc: "Country, city, device, browser, IP, hour-of-day heatmaps. Know exactly who scans your QR and when." },
  { icon: "📶", title: "WiFi QR", desc: "Customers scan and connect to your WiFi instantly. No typing passwords. Perfect for cafes and hotels." },
  { icon: "💳", title: "UPI Payment QR", desc: "Accept payments via PhonePe, GPay, Paytm. Scan opens the payment app with your details pre-filled." },
  { icon: "🍽️", title: "Smart Menu QR", desc: "Full digital menu with categories, photos, prices, veg/non-veg tags. Update items in real-time." },
  { icon: "🔒", title: "PIN Protection", desc: "Gate any QR behind a PIN code. Perfect for staff-only content, VIP access, exclusive offers." },
  { icon: "🎟️", title: "Dynamic Coupons", desc: "Each scan gets a unique coupon code from your list. No coupon sharing. Track every redemption." },
  { icon: "↔️", title: "A/B Split Testing", desc: "Split traffic 50/50 between two URLs. See which version wins with real scan data." },
  { icon: "⏰", title: "QR Expiry", desc: "Auto-deactivate after a date or max scans. Perfect for limited-time offers and event tickets." },
  { icon: "📁", title: "Folders & Search", desc: "Organize all your QR codes into color-coded folders. Search across all campaigns instantly." },
  { icon: "👥", title: "Team Access", desc: "Invite team members as Viewers or Editors. Manage everything from one agency account." },
];

const PLANS = [
  {
    name: "Free",
    price: { monthly: 0, yearly: 0 },
    features: ["5 QR codes", "Direct URL only", "No analytics", "No leads or feedback"],
    cta: "Start Free",
    highlight: false,
  },
  {
    name: "Pro",
    price: { monthly: 499, yearly: 4999 },
    features: ["50 QR codes", "All QR modes", "Full analytics", "Leads & Feedback", "WiFi, UPI, Menu", "PIN & Coupons", "A/B Testing"],
    cta: "Start 7-Day Free Trial",
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Agency",
    price: { monthly: 1499, yearly: 14999 },
    features: ["Unlimited QR codes", "Everything in Pro", "Bulk QR generation", "Team members", "Priority support", "Brand kit"],
    cta: "Start 7-Day Free Trial",
    highlight: false,
  },
];

const TESTIMONIALS = [
  { name: "Rahul Sharma", role: "Restaurant Owner, Mumbai", text: "The feedback QR alone saved my Google rating. Negative reviews go private now. My rating went from 3.8 to 4.6 in 3 months.", avatar: "RS" },
  { name: "Priya Mehta", role: "Marketing Agency, Bangalore", text: "We manage 200+ QR codes for clients. The folder system and bulk generator saves us hours every week. Agency plan pays for itself.", avatar: "PM" },
  { name: "Amit Gupta", role: "Event Organizer, Delhi", text: "Used Lead QRs at our expo. Captured 847 leads in one day. CSV export went straight into our CRM. Incredible.", avatar: "AG" },
];

const STATS = [
  { value: "2M+", label: "QR Scans Tracked" },
  { value: "12K+", label: "QR Codes Created" },
  { value: "98%", label: "Uptime Guaranteed" },
  { value: "7 Day", label: "Free Trial" },
];

function AnimatedCounter({ target }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const numeric = parseInt(target.replace(/[^0-9]/g, ""));
        let start = 0;
        const steps = 40;
        const increment = numeric / steps;
        const timer = setInterval(() => {
          start += increment;
          if (start >= numeric) { setCount(numeric); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 30);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);
  return <span ref={ref}>{count}</span>;
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item" onClick={() => setOpen(!open)}>
      <div className="faq-q">
        <span>{q}</span>
        <span style={{ color: "#ef4444", fontSize: 20, fontWeight: 300, transition: "transform 0.2s", transform: open ? "rotate(45deg)" : "none" }}>+</span>
      </div>
      {open && <div className="faq-a">{a}</div>}
    </div>
  );
}

export default function LandingPage() {
  const [billing, setBilling] = useState("monthly");
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const { currency, setCurrency, format, loading: ratesLoading } = useCurrency();

  useEffect(() => {
    document.title = "Dynamic QR Code Generator & Analytics Platform | Digitoly QR";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = "Create smart, editable dynamic QR codes for your business. Capture leads, filter customer reviews, display digital menus, and track scans with real-time analytics.";
    let metaKw = document.querySelector('meta[name="keywords"]');
    if (!metaKw) { metaKw = document.createElement('meta'); metaKw.name = 'keywords'; document.head.appendChild(metaKw); }
    metaKw.content = "dynamic qr code generator, trackable qr codes, qr code analytics, lead capture qr code, upi payment qr code, smart menu qr";
    const timer = setInterval(() => setActiveFeature(p => (p + 1) % FEATURES.length), 3000);
    return () => clearInterval(timer);
  }, []);

  const getDisplayPrice = (amountINR) => {
    if (amountINR === 0) return "Free";
    return ratesLoading ? `₹${amountINR.toLocaleString()}` : format(amountINR);
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Inter', sans-serif", background: "#020617", color: "white", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,700;0,9..40,900;1,9..40,900&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        .nav-link { color: #94a3b8; text-decoration: none; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; transition: color 0.2s; }
        .nav-link:hover { color: white; }
        .btn-primary { background: #ef4444; color: white; border: none; padding: 14px 28px; border-radius: 14px; font-size: 13px; font-weight: 900; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; transition: all 0.2s; text-decoration: none; display: inline-block; }
        .btn-primary:hover { background: #dc2626; transform: translateY(-1px); }
        .btn-secondary { background: transparent; color: white; border: 1px solid rgba(255,255,255,0.15); padding: 14px 28px; border-radius: 14px; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; transition: all 0.2s; text-decoration: none; display: inline-block; }
        .btn-secondary:hover { background: rgba(255,255,255,0.05); }
        .feature-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 24px; padding: 28px; transition: all 0.3s; cursor: default; }
        .feature-card:hover { background: rgba(255,255,255,0.06); border-color: rgba(239,68,68,0.3); transform: translateY(-2px); }
        .plan-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 28px; padding: 36px; transition: all 0.3s; }
        .plan-card.highlight { background: linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(168,85,247,0.08) 100%); border-color: rgba(239,68,68,0.4); }
        .testimonial-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 24px; padding: 32px; }
        .section { padding: 96px 24px; max-width: 1200px; margin: 0 auto; }
        .grid-2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px; }
        .grid-3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; }
        .grid-4 { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 20px; }
        .tag { display: inline-block; background: rgba(239,68,68,0.15); color: #fca5a5; border: 1px solid rgba(239,68,68,0.2); padding: 6px 14px; border-radius: 999px; font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 20px; }
        .section-title { font-size: clamp(32px, 5vw, 52px); font-weight: 900; line-height: 1.1; margin-bottom: 16px; }
        .section-sub { font-size: 16px; color: #64748b; line-height: 1.7; max-width: 560px; }
        .check { color: #22c55e; margin-right: 10px; font-size: 14px; }
        .avatar { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg, #ef4444, #7c3aed); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 900; color: white; flex-shrink: 0; }
        .glow { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.15; pointer-events: none; }
        .badge-live { display: inline-flex; align-items: center; gap: 6px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.2); color: #86efac; padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 700; }
        .dot-live { width: 6px; height: 6px; border-radius: 50%; background: #22c55e; animation: pulse 2s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        .mode-pill { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; padding: 8px 16px; font-size: 12px; font-weight: 700; color: #94a3b8; white-space: nowrap; }
        .scrolling-row { display: flex; gap: 12px; animation: scroll-left 25s linear infinite; width: max-content; }
        .scrolling-row-2 { display: flex; gap: 12px; animation: scroll-right 20s linear infinite; width: max-content; }
        @keyframes scroll-left { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes scroll-right { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
        .overflow-hidden-row { overflow: hidden; }
        .faq-item { border-bottom: 1px solid rgba(255,255,255,0.07); padding: 24px 0; cursor: pointer; }
        .faq-q { font-size: 16px; font-weight: 700; display: flex; justify-content: space-between; align-items: center; }
        .faq-a { font-size: 14px; color: #64748b; line-height: 1.7; margin-top: 12px; }
        .currency-select { appearance: none; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px; padding: 10px 36px 10px 14px; color: white; font-size: 13px; font-weight: 700; outline: none; cursor: pointer; transition: all 0.2s; }
        .currency-select:hover { background: rgba(255,255,255,0.1); }
        .currency-select option { background: #0f172a; color: white; }
        @media (max-width: 768px) {
          .section { padding: 64px 20px; }
          .hero-btns { flex-direction: column; }
          .hide-mobile { display: none !important; }
          .show-mobile { display: flex !important; }
          .pricing-controls { flex-direction: column; align-items: center; gap: 16px; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(2,6,23,0.9)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.05)", padding: "16px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
            <a href="/" style={{ textDecoration: "none" }}>
              <span style={{ fontSize: 22, fontWeight: 900, fontStyle: "italic", color: "white", letterSpacing: "-1px" }}>Digitoly QR</span>
            </a>
            <div className="hide-mobile" style={{ display: "flex", gap: 28 }}>
              <a href="#features" className="nav-link">Features</a>
              <a href="#how-it-works" className="nav-link">How it Works</a>
              <a href="#pricing" className="nav-link">Pricing</a>
              <a href="#faq" className="nav-link">FAQ</a>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Link to="/login" className="nav-link hide-mobile">Login</Link>
            <Link to="/login" className="btn-primary" style={{ padding: "10px 20px", fontSize: 12 }}>Start Free Trial</Link>
            <button onClick={() => setMenuOpen(!menuOpen)}
              style={{ display: "none", background: "none", border: "1px solid rgba(255,255,255,0.1)", padding: "8px 12px", borderRadius: 10, color: "white", cursor: "pointer", fontSize: 18 }}
              className="show-mobile">
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div style={{ position: "fixed", top: 60, left: 0, right: 0, bottom: 0, background: "#020617", zIndex: 99, padding: 32, display: "flex", flexDirection: "column", gap: 8, overflowY: "auto" }}>
          {[["Features", "#features"], ["How it Works", "#how-it-works"], ["Pricing", "#pricing"], ["FAQ", "#faq"]].map(([label, href]) => (
            <a key={label} href={href} onClick={() => setMenuOpen(false)}
              style={{ fontSize: 24, fontWeight: 900, fontStyle: "italic", color: "white", textDecoration: "none", padding: "16px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", textTransform: "uppercase", letterSpacing: "-0.5px" }}>
              {label}
            </a>
          ))}
          <Link to="/login" onClick={() => setMenuOpen(false)} className="btn-primary" style={{ marginTop: 24, display: "block", textAlign: "center" }}>
            Start Free Trial
          </Link>
        </div>
      )}

      {/* HERO */}
      <div style={{ position: "relative", overflow: "hidden", padding: "80px 24px 60px" }}>
        <div className="glow" style={{ width: 600, height: 600, background: "#ef4444", top: -200, left: "20%" }} />
        <div className="glow" style={{ width: 400, height: 400, background: "#7c3aed", top: 100, right: "10%" }} />
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          <div className="badge-live" style={{ marginBottom: 24, display: "inline-flex" }}>
            <div className="dot-live" />
            7-Day Free Trial · No Credit Card Required
          </div>
          <h1 style={{ fontSize: "clamp(40px, 7vw, 76px)" }}>
  Dynamic QR Code Generator<br />
  <span style={{ color: "#ef4444" }}>with Real Analytics</span><br />
  for Businesses Worldwide
</h1>
          <p style={{ fontSize: 18, color: "#64748b", lineHeight: 1.7, marginBottom: 40, maxWidth: 600, margin: "0 auto 40px" }}>
            Not just redirect links. Capture leads, filter reviews, show menus, accept UPI payments, connect WiFi — all from one dashboard with real-time analytics.
          </p>
          <div className="hero-btns" style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", marginBottom: 60 }}>
            <Link to="/login" className="btn-primary" style={{ fontSize: 14, padding: "16px 36px" }}>
              Start Free — 7 Days Full Access
            </Link>
            <a href="#how-it-works" className="btn-secondary" style={{ fontSize: 14, padding: "16px 36px" }}>
              See How It Works →
            </a>
          </div>
          <div className="grid-4" style={{ maxWidth: 700, margin: "0 auto" }}>
            {STATS.map((s, i) => (
              <div key={i} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 900, fontStyle: "italic", color: "white", letterSpacing: "-1px" }}>
                  {s.value.includes("+") ? (
                    <><AnimatedCounter target={s.value} />{s.value.includes("M") ? "M+" : "K+"}</>
                  ) : s.value}
                </div>
                <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SCROLLING PILLS */}
      <div style={{ padding: "40px 0", borderTop: "1px solid rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.05)", background: "rgba(255,255,255,0.01)" }}>
        <div className="overflow-hidden-row" style={{ marginBottom: 12 }}>
          <div className="scrolling-row">
            {[...["📍 Direct URL", "🎯 Lead Capture", "⭐ Review Filter", "📶 WiFi Connect", "💳 UPI Payment", "🍽️ Smart Menu", "📇 vCard", "🔒 PIN Protected", "↔️ A/B Testing", "⏰ Auto-Expiry", "🎟️ Dynamic Coupons", "📊 Analytics"], ...["📍 Direct URL", "🎯 Lead Capture", "⭐ Review Filter", "📶 WiFi Connect", "💳 UPI Payment", "🍽️ Smart Menu", "📇 vCard", "🔒 PIN Protected", "↔️ A/B Testing", "⏰ Auto-Expiry", "🎟️ Dynamic Coupons", "📊 Analytics"]].map((m, i) => (
              <span key={i} className="mode-pill">{m}</span>
            ))}
          </div>
        </div>
        <div className="overflow-hidden-row">
          <div className="scrolling-row-2">
            {[...["🗂️ Folder Organization", "👥 Team Access", "📧 Scan Notifications", "🔍 Health Monitor", "📦 Bulk Generator", "🎨 Brand Kit", "📈 Heatmaps", "🌍 Geo Analytics", "📱 Device Tracking", "🤖 Pixel Retargeting", "📋 CSV Export", "⚡ Real-Time Scans"], ...["🗂️ Folder Organization", "👥 Team Access", "📧 Scan Notifications", "🔍 Health Monitor", "📦 Bulk Generator", "🎨 Brand Kit", "📈 Heatmaps", "🌍 Geo Analytics", "📱 Device Tracking", "🤖 Pixel Retargeting", "📋 CSV Export", "⚡ Real-Time Scans"]].map((m, i) => (
              <span key={i} className="mode-pill">{m}</span>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div id="how-it-works" className="section">
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <div className="tag">How It Works</div>
          <h2 className="section-title">Create, Share, Track.<br />In Minutes.</h2>
          <p className="section-sub" style={{ margin: "0 auto" }}>No technical knowledge needed. If you can fill a form, you can create professional QR campaigns.</p>
        </div>
        <div className="grid-3">
          {[
            { step: "01", title: "Choose Your Mode", desc: "Pick from 8 QR types — Direct, Lead Capture, Feedback Filter, WhatsApp, WiFi, UPI, Menu, or vCard. Each is built for a specific business purpose." },
            { step: "02", title: "Customize & Design", desc: "Set your colors, add your logo, style the dots and eyes. Every QR is unique to your brand. Download as PNG, SVG, PDF, or JPG." },
            { step: "03", title: "Print, Share, Track", desc: "Put it on menus, receipts, posters, packaging, cards. Every scan is tracked in real-time with country, device, city, and hour data." },
          ].map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: 64, fontWeight: 900, fontStyle: "italic", color: "rgba(239,68,68,0.7)", letterSpacing: "-4px", lineHeight: 1, marginBottom: 16 }}>{s.step}</div>
              <h3 style={{ fontSize: 20, fontWeight: 900, marginBottom: 12 }}>{s.title}</h3>
              <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.7 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURES */}
      <div id="features" style={{ background: "rgba(255,255,255,0.01)", borderTop: "1px solid rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="section">
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div className="tag">All Features</div>
            <h2 className="section-title">Everything Your Business<br />Needs From a QR Platform</h2>
            <p className="section-sub" style={{ margin: "0 auto" }}>12 powerful features. One affordable subscription. Replaces 5 different tools you're already paying for.</p>
          </div>
          <div className="grid-3">
            {FEATURES.map((f, i) => (
              <div key={i} className="feature-card">
                <div style={{ fontSize: 32, marginBottom: 16 }}>{f.icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 900, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.7 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* USE CASES */}
      <div className="section">
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <div className="tag">Use Cases</div>
          <h2 className="section-title">Built for Every Business</h2>
        </div>
        <div className="grid-3">
          {[
            { emoji: "🍽️", title: "Restaurants & Cafes", items: ["Digital menu QR on every table", "Google review filter (4-5 ⭐ go public)", "WiFi QR for customers", "Lead capture for loyalty programs"] },
            { emoji: "🏬", title: "Retail & E-Commerce", items: ["UPI payment QR at checkout", "Product feedback collection", "Coupon QR for promotions", "Retargeting pixels on every scan"] },
            { emoji: "📣", title: "Marketing Agencies", items: ["Manage 100s of client QRs in folders", "Bulk generate QR codes from CSV", "White-label analytics for clients", "Team access with editor/viewer roles"] },
            { emoji: "🎪", title: "Events & Exhibitions", items: ["Lead capture at your booth", "Auto-expire after event date", "Max-scan limits for limited offers", "Real-time scan count dashboard"] },
            { emoji: "🏥", title: "Healthcare & Clinics", items: ["Patient feedback collection", "PIN-protected for sensitive content", "vCard QR for doctor contacts", "Time-based routing (clinic hours)"] },
            { emoji: "🏨", title: "Hotels & Hospitality", items: ["WiFi QR in every room", "Feedback QR at checkout", "Smart menu for room service", "Scan notifications for staff"] },
          ].map((u, i) => (
            <div key={i} className="feature-card">
              <div style={{ fontSize: 32, marginBottom: 12 }}>{u.emoji}</div>
              <h3 style={{ fontSize: 15, fontWeight: 900, marginBottom: 16, textTransform: "uppercase", letterSpacing: "0.5px" }}>{u.title}</h3>
              <ul style={{ listStyle: "none" }}>
                {u.items.map((item, j) => (
                  <li key={j} style={{ fontSize: 13, color: "#64748b", padding: "4px 0", display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span className="check">✓</span>{item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* TESTIMONIALS */}
      <div style={{ background: "rgba(255,255,255,0.01)", borderTop: "1px solid rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="section">
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div className="tag">Testimonials</div>
            <h2 className="section-title">Businesses Love Digitoly QR</h2>
          </div>
          <div className="grid-3">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="testimonial-card">
                <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
                  {[...Array(5)].map((_, j) => <span key={j} style={{ color: "#fbbf24", fontSize: 16 }}>★</span>)}
                </div>
                <p style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.8, marginBottom: 20, fontStyle: "italic" }}>"{t.text}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="avatar">{t.avatar}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 900 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: "#475569" }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PRICING */}
      <div id="pricing" className="section">
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div className="tag">Pricing</div>
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-sub" style={{ margin: "0 auto 40px" }}>Start with a 7-day free trial. No credit card required. Cancel anytime.</p>

          {/* Billing + Currency controls */}
          <div className="pricing-controls" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
            {/* Billing toggle */}
            <div style={{ display: "inline-flex", background: "rgba(255,255,255,0.05)", borderRadius: 14, padding: 4, gap: 4 }}>
              {["monthly", "yearly"].map(b => (
                <button key={b} onClick={() => setBilling(b)}
                  style={{ padding: "10px 24px", borderRadius: 10, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", transition: "all 0.2s", background: billing === b ? "white" : "transparent", color: billing === b ? "#020617" : "#64748b" }}>
                  {b}{b === "yearly" && <span style={{ marginLeft: 8, background: "#22c55e", color: "white", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 900 }}>SAVE 17%</span>}
                </button>
              ))}
            </div>

            {/* Currency selector */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: "#64748b", fontWeight: 700, whiteSpace: "nowrap" }}>Show prices in:</span>
              <div style={{ position: "relative" }}>
                <select
                  value={currency}
                  onChange={e => setCurrency(e.target.value)}
                  className="currency-select"
                >
                  {[
                    { code: "INR", label: "🇮🇳 INR — Indian Rupee" },
                    { code: "USD", label: "🇺🇸 USD — US Dollar" },
                    { code: "GBP", label: "🇬🇧 GBP — British Pound" },
                    { code: "EUR", label: "🇪🇺 EUR — Euro" },
                    { code: "AUD", label: "🇦🇺 AUD — Australian Dollar" },
                    { code: "CAD", label: "🇨🇦 CAD — Canadian Dollar" },
                    { code: "NZD", label: "🇳🇿 NZD — New Zealand Dollar" },
                    { code: "CHF", label: "🇨🇭 CHF — Swiss Franc" },
                  ].map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Currency disclaimer */}
          {currency !== "INR" && (
            <p style={{ fontSize: 12, color: "#475569", marginTop: 16, fontStyle: "italic" }}>
              * Prices shown are approximate. Payment is charged in INR equivalent via Razorpay.
            </p>
          )}
        </div>

        <div className="grid-3" style={{ alignItems: "start" }}>
          {PLANS.map((p, i) => (
            <div key={i} className={`plan-card ${p.highlight ? "highlight" : ""}`} style={{ position: "relative" }}>
              {p.badge && (
                <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "#ef4444", color: "white", padding: "4px 20px", borderRadius: 999, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px", whiteSpace: "nowrap" }}>
                  {p.badge}
                </div>
              )}
              <h3 style={{ fontSize: 13, fontWeight: 900, textTransform: "uppercase", letterSpacing: "2px", color: "#64748b", marginBottom: 16 }}>{p.name}</h3>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 44, fontWeight: 900, fontStyle: "italic", letterSpacing: "-2px" }}>
                  {getDisplayPrice(p.price[billing])}
                </span>
                {p.price[billing] > 0 && <span style={{ fontSize: 14, color: "#64748b", marginLeft: 4 }}>/{billing === "monthly" ? "mo" : "yr"}</span>}
              </div>
              {/* Show INR equivalent when foreign currency selected */}
              {p.price[billing] > 0 && currency !== "INR" && (
                <p style={{ fontSize: 11, color: "#475569", marginBottom: 20 }}>≈ ₹{p.price[billing].toLocaleString()} INR</p>
              )}
              {p.price[billing] === 0 && <div style={{ marginBottom: 20 }} />}
              <ul style={{ listStyle: "none", marginBottom: 32 }}>
                {p.features.map((f, j) => (
                  <li key={j} style={{ padding: "8px 0", fontSize: 14, color: "#94a3b8", display: "flex", alignItems: "center", gap: 10, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <span className="check">✓</span>{f}
                  </li>
                ))}
              </ul>
              <Link to="/login" className={p.highlight ? "btn-primary" : "btn-secondary"} style={{ display: "block", textAlign: "center", width: "100%" }}>
                {p.cta}
              </Link>
              {p.name !== "Free" && <p style={{ textAlign: "center", fontSize: 11, color: "#64748b", marginTop: 12 }}>7-day free trial · No credit card</p>}
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div id="faq" style={{ background: "rgba(255,255,255,0.01)", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="section" style={{ maxWidth: 720 }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div className="tag">FAQ</div>
            <h2 className="section-title">Common Questions</h2>
          </div>
          {[
            { q: "What happens after the 7-day trial?", a: "Your account moves to the Free plan — 5 QR codes, direct mode only. Your existing QRs keep working. Upgrade anytime to restore full access." },
            { q: "Can I change the destination URL after printing?", a: "Yes. Dynamic QR codes (all paid modes) let you change the destination URL anytime from your dashboard. The printed QR code instantly starts going to the new URL." },
            { q: "Does the QR code stop working if I cancel?", a: "Free plan QRs continue working after cancellation. Paid features (leads forms, feedback filters, etc.) will revert to basic redirects. Your data is never deleted." },
            { q: "How does the feedback QR protect my Google rating?", a: "When a customer rates 4-5 stars, they're sent directly to your Google review page. 1-3 stars shows them a private form. You get notified and can resolve the issue before it becomes a public negative review." },
            { q: "Is there a setup fee or contract?", a: "No setup fee, no contract. Monthly plans can be cancelled anytime. Yearly plans are billed once and you save 17%." },
            { q: "Can I use Digitoly QR for multiple clients?", a: "Yes. The Agency plan supports unlimited QR codes, team member access, folder organization, and bulk generation — built for agencies managing multiple clients." },
            { q: "What currencies can I pay in?", a: "All payments are processed in INR via Razorpay. We show approximate prices in USD, GBP, EUR, AUD, CAD, NZD, and CHF for reference — the exact INR amount is charged at checkout." },
            { q: "What payment methods do you accept?", a: "All UPI apps (PhonePe, GPay, Paytm), credit/debit cards, and net banking via Razorpay. 100% secure payment processing." },
          ].map((item, i) => <FaqItem key={i} q={item.q} a={item.a} />)}
        </div>
      </div>

      {/* CTA BANNER */}
      <div style={{ position: "relative", overflow: "hidden", padding: "80px 24px", textAlign: "center" }}>
        <div className="glow" style={{ width: 500, height: 500, background: "#ef4444", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <h2 style={{ fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 900, fontStyle: "italic", letterSpacing: "-1.5px", marginBottom: 20 }}>
            Start Your Free Trial Today
          </h2>
          <p style={{ fontSize: 16, color: "#64748b", marginBottom: 36 }}>
            7 days of full Agency access. No credit card. No commitment.
          </p>
          <Link to="/login" className="btn-primary" style={{ fontSize: 15, padding: "18px 48px" }}>
            Create Your First QR — Free
          </Link>
          <p style={{ fontSize: 12, color: "#334155", marginTop: 16 }}>Takes 60 seconds to set up</p>
        </div>
      </div>

{/* SEO content block — screen reader accessible, not visually prominent */}
<div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px 0", color: "#334155", fontSize: 13, lineHeight: 1.8 }}>
  <h2 style={{ fontSize: 18, fontWeight: 700, color: "#475569", marginBottom: 12 }}>
    Dynamic QR Code Generator for Global Businesses
  </h2>
  <p>
    Digitoly QR is a professional dynamic QR code generator built for restaurants, retailers, agencies, 
    and event organizers across the world. Unlike basic QR code tools, every QR code you create on Digitoly 
    is trackable, editable, and purpose-built for business outcomes.
  </p>
  <p style={{ marginTop: 12 }}>
    Our platform supports 8 QR code types: Direct URL QR codes, Lead Capture QR codes, Google Review 
    Filter QR codes, WhatsApp QR codes, WiFi QR codes, UPI Payment QR codes, Smart Menu QR codes, 
    and vCard digital business card QR codes. Each scan is tracked with country, city, device type, 
    browser, IP address, and hour of day.
  </p>
  <p style={{ marginTop: 12 }}>
    Restaurants use our Smart Menu QR and Google Review Filter. Retailers use UPI Payment QR and 
    Dynamic Coupon QR. Marketing agencies use Bulk QR Generation and Team Collaboration. 
    Events use Lead Capture QR with auto-expiry. Hotels use WiFi QR and Feedback QR.
  </p>
  <p style={{ marginTop: 12 }}>
    Plans start free with a 7-day trial. Pro plan at ₹499/month or ₹4,999/year. 
    Agency plan at ₹1,499/month or ₹14,999/year. All payments via Razorpay — UPI, cards, net banking.
  </p>
</div>

      {/* FOOTER */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "48px 32px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", gap: 48, justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, fontStyle: "italic", letterSpacing: "-1px", marginBottom: 12 }}>Digitoly QR</div>
            <p style={{ fontSize: 13, color: "#f4f6fa", maxWidth: 260, lineHeight: 1.7 }}>Smart QR codes for modern businesses. Built in India. Used worldwide.</p>
          </div>
          <div style={{ display: "flex", gap: 64, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.5px", color: "#ffffff", marginBottom: 16 }}>PRODUCT</div>
              {["Features", "Pricing", "Help Center"].map(l => (
                <a key={l} href={l === "Help Center" ? "/help" : `#${l.toLowerCase()}`}
                  style={{ display: "block", fontSize: 13, color: "#64748b", textDecoration: "none", marginBottom: 10, transition: "color 0.2s" }}
                  onMouseOver={e => e.target.style.color = "white"} onMouseOut={e => e.target.style.color = "#64748b"}>{l}</a>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.5px", color: "#ffffff", marginBottom: 16 }}>COMPANY</div>
              {[["About", "/p/about"], ["Contact", "/p/contact"], ["Privacy Policy", "/privacy"], ["Terms of Service", "/terms"]].map(([l, href]) => (
                <a key={l} href={href}
                  style={{ display: "block", fontSize: 13, color: "#64748b", textDecoration: "none", marginBottom: 10, transition: "color 0.2s" }}
                  onMouseOver={e => e.target.style.color = "white"} onMouseOut={e => e.target.style.color = "#64748b"}>{l}</a>
              ))}
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1200, margin: "32px auto 0", paddingTop: 24, borderTop: "1px solid rgba(245, 237, 237, 0.05)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <p style={{ fontSize: 12, color: "#d7dbe2" }}>© {new Date().getFullYear()} Digitoly QR · All Rights Reserved · <a href="https://www.digitoly.com" style={{ color: "#ff0000", textDecoration: "none" }}>DIGITOLY CONSULTING</a></p>
          <p style={{ fontSize: 12, color: "#d7dbe2" }}>Made with ❤️ in India</p>
        </div>
      </footer>
    </div>
  );
}